<template>
    <CustomTable
        :module-name="moduleName"
        :module-name-singular="moduleNameSingular"
        :tableHeaders="tableHeaders"
        :filters="dataImportFilters"
        :bulkOperations="dataImportBulkOperations"
        :menuOptions="menuOptions"
        :defaultOrdering="defaultOrdering"
        :response="response"
        :default-page-size="100"
        :should-reload="shouldReload"
        :shouldClearSelected="shouldClearSelected"
        :bulkOperationResponse="bulkOperationResponse"
        createModalSize="w-1/3 h-1/2"
        @select-tab="selectViewType"
        @refetch-results="loadImportedCompanies"
        @perform-bulk-action="performCompanyBulkAction"
    >
    <template #bulk-operations="{ events: { hideBulkActionMenu, hideBulkOperations, clearSelectAllClearSelectedResults, refetchResults }, bulkAction, fetchFilter, selectedResults }">
        <DataImportBulkOperations
            :bulkAction="bulkAction"
            :selectedResults="selectedResults"
            :view-type="viewType"
            :data-request="dataRequest"
            :data-imports="dataImports"
            :fetch-filter="fetchFilter"
            @cancel-bulk-operation="hideBulkActionMenu"
            @close-bulk-operation="hideBulkOperations"
            @clear-selected-results="clearSelectAllClearSelectedResults"
            @refetch-results="refetchResults(fetchFilter)"
        />
    </template>
    <template #create-form="{ events: { closeCreateForm, refetchResults }, fetchFilter }">
        <CreateDataImport
            :data-source-options="dataSourceOptions"
            :client-options="clientOptions"
            @refetch-results="refetchResults(fetchFilter)"
            @close="closeCreateForm"
        />
    </template>
    <template v-if="viewType=='imports'" #table-row="{ events: { addSelectedResult, removeSelectedResult, refetchResults }, selectAll, fetchFilter, userPermissions }">
        <DataImportRow
            v-for="dataImport in dataImports"
            :key="dataImport.id"
            :view-type="viewType"
            :dataImport="dataImport"
            :selectAll="selectAll"
            :userPermissions="userPermissions"
            @refetch-results="refetchResults(fetchFilter)"
            @select-row="addSelectedResult"
            @deselect-row="removeSelectedResult"
        />
    </template>
    <template v-else-if="viewType=='companies'" #table-row="{ events: { addSelectedResult, removeSelectedResult, refetchResults }, selectAll, fetchFilter, userPermissions }">
        <DataImportCompanyRow
            v-for="company in dataImportCompanies" 
            :key="company.id" 
            :company="company" 
            :selectAll="selectAll"
            :userPermissions="userPermissions"
            @refetch-results="refetchResults(fetchFilter)"
            @select-row="addSelectedResult"
            @deselect-row="removeSelectedResult"
        />
        <tr class="bg-gray-200 h-60">
            <td :colspan="columnSpan" :class="{ 'border-t border-gray-300' : results && results.length > 0}"></td>
        </tr>
    </template>
    <template #table-footer="{ events: { getNext, goToFirst, getPrev, goToLast, jumpToPage, setPageSizeFilter }, viewingFromNbr, viewingToNbr, count, selectedResults, totalPages, page, results, next, prev }">
        <DataImportFooter
            :is-loading="isLoading"
            :view-type="viewType"
            :viewingFromNbr="viewingFromNbr"
            :viewingToNbr="viewingToNbr"
            :count="count"
            :selectedResults="selectedResults"
            :numPages="totalPages"
            :results="results"
            :page="page"
            :has-next="next"
            :has-prev="prev"
            @get-next="getNext"
            @go-to-first="goToFirst"
            @get-prev="getPrev"
            @go-to-last="goToLast"
            @jump-to-page="jumpToPage"
            @set-page-size-filter="setPageSizeFilter"
        />
    </template>
    </CustomTable>
</template>

<script setup>
import { defineProps, ref, computed, reactive } from 'vue';
import DataImportRow from './DataImportRow.vue';
import DataImportCompanyRow from './DataImportCompanyRow.vue';
import DataImportBulkOperations from './DataImportBulkOperations.vue';
import CreateDataImport from './CreateDataImport.vue';
import DataImportFooter from './DataImportFooter.vue';
import CustomTable from '../base-components/CustomTable.vue';
import { getCollectionTags } from '@/services/CollectionTag';
import { getCompaniesByQueryParams, performBulkAction, performBulkActionOnQuery } from '@/services/Company.js';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';


const store = useStore()
const $toast = useToast()
const props = defineProps(['dataRequest'])
const isLoading = ref(false)
const shouldClearSelected = ref(false)
const bulkOperationResponse = ref()
const moduleName = ref('Data Imports')
const moduleNameSingular = ref('Data Import');
const viewType = ref('imports')
const shouldReload = ref(false)


const defaultOrdering = ref([])

const dataImports = computed(() => {
    return props.dataRequest && props.dataRequest.data_imports ? props.dataRequest.data_imports : [];
})

// const response = ref(null)

const companyResponse = ref(null)
const importResponse = ref({
        status: 200,
        data: {
            results: dataImports.value,
            next: null,
            previous: null,
            count: dataImports.value.length,
            page: 1,
            num_pages: Math.ceil(dataImports.value.length / 200),
        }
    })

const dataImportCompanies = ref([])

const importHeaders = ref([
    {
        value: 'import_date',
        label: 'Import Date',
        class: 'p-0 w-[15%] bg-[#ECEEF1] border-b-2 h-7',
        orderFieldName: "data_imports"
    },
    {
        value: 'import_name',
        label: 'Import Name',
        class: 'p-0 w-[20%] bg-[#ECEEF1] border-b-2 h-7',
        orderFieldName: "import_name"
    },
    {
        value: 'import_type',
        label: 'Type',
        class: 'p-0 w-[15%] bg-[#ECEEF1] border-b-2 h-7',
    },
    {
        value: 'import_url',
        label: 'URL',
        class: 'p-0 w-[25%] bg-[#ECEEF1] border-b-2 h-7',
    },
    {
        value: 'import_script',
        label: 'Script',
        class: 'p-0 w-[15%] bg-[#ECEEF1] border-b-2 h-7',
    },
    {
        value: 'import_pattern',
        label: 'Pattern',
        class: 'p-0 w-[10%] bg-[#ECEEF1] border-b-2 h-7',
    },
    {
        value: 'number_imported_rows',
        label: '# Imported Rows',
        class: 'p-0 w-[10%] bg-[#ECEEF1] border-b-2 h-7',
    },
])

const companyHeaders = ref([
    {
        value: 'data_imports',
        label: 'Import Date',
        class: 'p-0 w-[15%] bg-[#ECEEF1] border-b-2 h-7',
        orderFieldName: "data_imports"
    },
    {
        value: 'id',
        label: 'ID',
        class: 'p-0 w-[10%] bg-[#ECEEF1] border-b-2 h-7',
        orderFieldName: "id"
    },
    {
        value: 'company_name',
        label: 'Company Name',
        class: 'p-0 w-[20%] bg-[#ECEEF1] border-b-2 h-7',
        orderFieldName: "company_name"
    },
    {
        value: 'tier_object',
        label: 'Tier',
        class: 'p-0 w-[10%] bg-[#ECEEF1] border-b-2 h-7',
    },
    {
        value: 'website_url',
        label: 'Website URL',
        class: 'p-0 w-[20%] bg-[#ECEEF1] border-b-2 h-7',
    },
    {
        value: 'location',
        label: 'Location',
        class: 'p-0 w-[15%] bg-[#ECEEF1] border-b-2 h-7',
    },
    {
        value: 'revenue',
        label: 'Revenue',
        class: 'p-0 w-[10%] bg-[#ECEEF1] border-b-2 h-7',
    },
])


const dataImportFilters = reactive({
    dataImport: {
        name: 'data_import',
        label: "Data Imports",
        value: dataImports.value.map((dataImport) => dataImport.id),
        defaultValue: dataImports.value.map((dataImport) => dataImport.id),
        options: [
            dataImports.value.map((dataImport) => dataImport.id)
        ],
        widget: 'list-box',
        multiSelect: true,
        visible: false,
    }
})

const dataImportBulkOperations = reactive({
    assignAllToCollection: {
        id: 1,
        label: 'Assign All Results To Collection',
        modelPath: 'companies',
        endpoint: 'bulk_assign_query_results_to_collection_tag',
        message: '',
        requireSelectedResults: false,
        payloadType: 'form',
        inputs: {
            collectionTag: {
                key: 'collection_tag_name',
                label: "Choose Collection Tag",
                value: [],
                defaultValue: [],
                required: true,
                visible: true,
                options: [
                    {value: "clear", label: "------------"}
                ],
                widget: 'datalist',
                multiSelect: true,
                showSelectedInputValues: true,
            }
        },
        pinned: false
    },
    assignToCollection: {
        id: 2,
        label: 'Assign To Collection',
        modelPath: 'companies',
        endpoint: 'bulk_assign_collection_tag',
        message: '',
        requireSelectedResults: true,
        payloadType: 'simple',
        inputs: {
            collectionTag: {
                label: "Choose Collection Tag",
                value: [],
                defaultValue: [],
                required: false,
                visible: true,
                options: [
                    {value: "clear", label: "------------"}
                ],
                widget: 'datalist',
                multiSelect: true,
                showSelectedInputValues: true,
            }
        },
        pinned: false,
    },
    // assignAllToCollection: {
    //     label: 'Assign All Results to Collection',
    //     modelPath: 'companies',
    //     endpoint: 'bulk_assign_query_results_to_collection_tag',
    //     message: '',
    //     requireSelectedResults: false,
    //     inputs: {
    //         collectionTag: {
    //             label: "Choose Collection Tag:",
    //             value: [],
    //             defaultValue: [],
    //             options: [
    //                 {value: "clear", label: "------------"}
    //             ],
    //             widget: 'datalist',
    //         }
    //     }
    // },
    // assignSelectedResultsToCollection: {
    //     label: 'Assign Selected Results to Collection',
    //     modelPath: 'companies',
    //     endpoint: 'bulk_assign_collection_tag',
    //     message: '',
    //     requireSelectedResults: true,
    //     inputs: {
    //         collectionTag: {
    //             label: "Choose Collection Tag:",
    //             value: [],
    //             defaultValue: [],
    //             options: [
    //                 {value: "clear", label: "------------"}
    //             ],
    //             widget: 'datalist',
    //         }
    //     }
    // },
})

const menuOptions = reactive({
    quickSearch: false,
    selectAll: false,
    selectAllHeader: true,
    paginationArrows: false,
    totalResults: false,
    pageSize: false,
    refreshButton: false,
    filters: false,
    controlPanel: {
        width: 'w-[330px]',
        panes: ['bulkOperations']
    },
    createForm: false,
    addFormRows: false,
    tabs: {
        imports: {
            label: 'Data Imports', isSelected: true
        },
        companies: {
            label: 'Imported Companies', isSelected: false
        },
    },
    tabChangeClearsSelected: true,
})

const tableHeaders = computed(() => {
    if(viewType.value == 'imports') {
        return importHeaders.value
    } else if(viewType.value == 'companies') {
        return companyHeaders.value
    } else {
        return []
    }
})

const response = computed(() => {
    if(viewType.value == 'imports') {
        return importResponse.value
    } else if(viewType.value == 'companies') {
        return companyResponse.value
    } else {
        return null
    }
})

function selectViewType(viewTypeValue) {
    viewType.value = viewTypeValue
    if(viewTypeValue == 'companies') {
        reloadDataImportCompanies()
    }
}

function reloadDataImportCompanies() {
    console.log('reloadDataImportCompanies')
    shouldReload.value = !shouldReload.value
}

function handleBulkActionResponse(actionData, response) {
    // console.log('handleResponse', response)
    if(response.status == 200) {
        $toast.open({
            message:`Completed bulk operation: ${actionData.label}`,
            type: 'success',
            duration: 3000
        })
        if(!actionData.keepSelected) {
            clearSelectedResults()
        }
        reloadDataImportCompanies()
        fetchAllOptions()
    } else {
        $toast.open({
            message: `Could not complete bulk operation: ${actionData.label}`,
            type: 'error',
            duration: 10000
        })
    }
    bulkOperationResponse.value = response
}

function clearSelectedResults() {
    console.log('bulkAction - clearSelectedResults')
    shouldClearSelected.value = !shouldClearSelected.value
}

function fetchAllOptions() {
    setCollectionTagOptions()
}

async function setCollectionTagOptions() {
    const token = store.state.auth.token
    const response = await getCollectionTags(token)
    dataImportBulkOperations.assignAllToCollection.inputs.collectionTag.options = [{value: "clear", label: "------------"}, ...response.map((collectionTag) => {
        return {value: collectionTag.collection_tag_name, label: collectionTag.collection_tag_name }
    })]
    dataImportBulkOperations.assignToCollection.inputs.collectionTag.options = [{value: "clear", label: "------------"}, ...response.map((collectionTag) => {
        return {value: collectionTag.collection_tag_name, label: collectionTag.collection_tag_name }
    })]
}

async function loadImportedCompanies(fetchFilter='') {
    isLoading.value = true
    let token = store.state.auth.token
    companyResponse.value = await getCompaniesByQueryParams(token, fetchFilter)
    dataImportCompanies.value = companyResponse.value.data.results
    isLoading.value = false
}

async function performCompanyBulkAction(bulkActionData) {
    // console.log('performCompanyBulkAction')
    // console.log(bulkActionData)
    if(bulkActionData.overrideAction) {
        dataImportBulkOperations[bulkActionData.key].performBulkAction(bulkActionData)
    } else {
        let response
        const token = store.state.auth.token
        if(bulkActionData.type == 'onSelected' && bulkActionData.selectedResults.length > 0) {
            const selectedResultIds = bulkActionData.selectedResults.map(({id}) => id)
            response = await performBulkAction(token, selectedResultIds, bulkActionData.endpoint, bulkActionData.payload)
        } else if (bulkActionData.type == 'onQuery' && bulkActionData.queryParams) {
            response = await performBulkActionOnQuery(token, bulkActionData.payload, bulkActionData.endpoint, bulkActionData.queryParams)
        }
        if(response) {
            handleBulkActionResponse(bulkActionData, response)
        }
    }
}

fetchAllOptions()
</script> 